// The away seam on the web (#403): when the tab is hidden or the window loses focus, note the time and the last message
// in view; when the person is back, the page asks the shared rule (shared/src/awaySeam.ts) whether a seam shows.
import { awayLoadsBack, awaySeam, awaySeamLabel, type AwayMessage } from "@bloom/shared";

/** Where the person was when they went away. */
export interface AwayMark {
  channelId: string;
  /** Unix ms. */
  leftAt: number;
  /** The newest message seen then: the list's end, or the last row in view when scrolled up. */
  seenSeq: number;
}

/**
 * After coming back, the seam is worked out again for this long whenever the channel's messages change: a reconnect
 * after a long absence brings what arrived meanwhile a moment after the tab shows.
 */
export const AWAY_SETTLE_MS = 10_000;

const clock = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

/**
 * Calls `onBack` when the person is back after being away: the tab was hidden, or the window had lost focus (the focus
 * can sit in a frame of the page: that is still here). `mark` says where they are at the moment they leave, and null
 * when there is nothing to note (no channel is open). Whether the time away is long enough is the shared rule's.
 * Returns what stops the watch.
 */
export function watchAway(opts: { mark: () => { channelId: string; seenSeq: number } | null; onBack: (mark: AwayMark, backAt: number) => void; now?: () => number }): () => void {
  const now = opts.now ?? Date.now;
  let left: AwayMark | null = null;
  const check = () => {
    if (document.visibilityState !== "visible" || !document.hasFocus()) {
      // The first of hidden and blur starts it; the second changes nothing.
      if (!left) {
        const here = opts.mark();
        if (here) left = { ...here, leftAt: now() };
      }
    } else if (left) {
      const was = left;
      left = null;
      opts.onBack(was, now());
    }
  };
  document.addEventListener("visibilitychange", check);
  addEventListener("blur", check);
  addEventListener("focus", check);
  return () => {
    document.removeEventListener("visibilitychange", check);
    removeEventListener("blur", check);
    removeEventListener("focus", check);
  };
}

/** The channel as held now. `hasOlder` is the room's `more`; `lastSeq` is the channel's newest seq. */
export interface AwayHere {
  channelId: string | null;
  me: string | null;
  messages: readonly AwayMessage[];
  hasOlder?: boolean;
  lastSeq?: number;
}

/**
 * The NEW line's new place and words, or null for no seam: the person is back in the channel they left, and the shared
 * rule finds messages that arrived meanwhile. It gives none while the held messages do not reach back to the last one
 * seen (`awayLoadBack` says to load them).
 */
export function awayDivider(mark: AwayMark, backAt: number, here: AwayHere): { after: number; label: string } | null {
  if (here.channelId !== mark.channelId) return null;
  const seam = awaySeam({ leftAt: mark.leftAt, backAt, seenSeq: mark.seenSeq, ...here });
  return seam && { after: seam.after, label: awaySeamLabel(clock.format(mark.leftAt), seam.count) };
}

/**
 * The target for `room.loadBack` when more than the newest page arrived while away: the held messages start after the
 * last one seen, older ones exist, and not too many are missing. Null when there is nothing to load.
 */
export function awayLoadBack(mark: AwayMark, backAt: number, here: AwayHere): { seq: number } | null {
  if (here.channelId !== mark.channelId) return null;
  return awayLoadsBack({ leftAt: mark.leftAt, backAt, seenSeq: mark.seenSeq, ...here }) ? { seq: mark.seenSeq } : null;
}
